// Shared background presets + painters for the manual image editor and its
// Advance Edit modal. Backgrounds are persisted non-destructively as a string
// token in the saved record's `background` field:
//   solid:    'white' | 'blue' | ... | 'transparent' | '#rrggbb'
//   gradient: 'gradient:<id>'
//   pattern:  'pattern:<id>'
//   preset:   'preset:<id>'      (image from files.sohozkaj.com/remove-bg-presets)
//   image:    'image'            (user upload — baked into the photo on save)

// ── Gradient presets — painted on canvas + previewed via matching CSS ──────────
export const BG_GRADIENTS = [
    { id: 'g_blue',  css: 'linear-gradient(135deg,#1e3a8a,#60a5fa)', type: 'linear', angle: 135, stops: [[0, '#1e3a8a'], [1, '#60a5fa']] },
    { id: 'g_grey',  css: 'radial-gradient(circle at 50% 35%,#e5e7eb,#6b7280)', type: 'radial', stops: [[0, '#e5e7eb'], [1, '#6b7280']] },
    { id: 'g_sky',   css: 'linear-gradient(180deg,#bae6fd,#0284c7)', type: 'linear', angle: 180, stops: [[0, '#bae6fd'], [1, '#0284c7']] },
    { id: 'g_warm',  css: 'linear-gradient(135deg,#fde68a,#f59e0b)', type: 'linear', angle: 135, stops: [[0, '#fde68a'], [1, '#f59e0b']] },
    { id: 'g_rose',  css: 'linear-gradient(135deg,#fbcfe8,#be185d)', type: 'linear', angle: 135, stops: [[0, '#fbcfe8'], [1, '#be185d']] },
    { id: 'g_teal',  css: 'radial-gradient(circle at 50% 35%,#99f6e4,#0f766e)', type: 'radial', stops: [[0, '#99f6e4'], [1, '#0f766e']] },
];

export function paintGradient(ctx, x, y, w, h, g) {
    let grad;
    if (g.type === 'radial') {
        grad = ctx.createRadialGradient(x + w / 2, y + h * 0.35, 0, x + w / 2, y + h / 2, Math.max(w, h) * 0.75);
    } else {
        const rad = (g.angle || 180) * Math.PI / 180;
        const ux = Math.sin(rad), uy = -Math.cos(rad);
        const half = (Math.abs(ux) * w + Math.abs(uy) * h) / 2;
        const cx = x + w / 2, cy = y + h / 2;
        grad = ctx.createLinearGradient(cx - ux * half, cy - uy * half, cx + ux * half, cy + uy * half);
    }
    g.stops.forEach(([o, c]) => grad.addColorStop(o, c));
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);
}

// ── Repeating pattern presets — each builds a small tile, drawn via createPattern ──
export const BG_PATTERNS = [
    { id: 'p_dots', tile: 18, base: '#f1f5f9', draw(t, s) { t.fillStyle = '#cbd5e1'; t.beginPath(); t.arc(s / 2, s / 2, s * 0.16, 0, Math.PI * 2); t.fill(); } },
    { id: 'p_grid', tile: 20, base: '#f8fafc', draw(t, s) { t.strokeStyle = '#cbd5e1'; t.lineWidth = 1; t.strokeRect(0.5, 0.5, s - 1, s - 1); } },
    { id: 'p_diag', tile: 16, base: '#eef2ff', draw(t, s) { t.strokeStyle = '#c7d2fe'; t.lineWidth = 2; t.beginPath(); t.moveTo(0, s); t.lineTo(s, 0); t.stroke(); } },
    { id: 'p_cross', tile: 22, base: '#fafaf9', draw(t, s) { t.strokeStyle = '#d6d3d1'; t.lineWidth = 1.5; t.beginPath(); t.moveTo(s / 2, s * 0.3); t.lineTo(s / 2, s * 0.7); t.moveTo(s * 0.3, s / 2); t.lineTo(s * 0.7, s / 2); t.stroke(); } },
];

export function buildPatternTile(p) {
    const c = document.createElement('canvas');
    c.width = p.tile; c.height = p.tile;
    const t = c.getContext('2d');
    t.fillStyle = p.base; t.fillRect(0, 0, p.tile, p.tile);
    p.draw(t, p.tile);
    return c;
}

export function paintPattern(ctx, x, y, w, h, p) {
    const pat = ctx.createPattern(buildPatternTile(p), 'repeat');
    ctx.save();
    ctx.translate(x, y);
    ctx.fillStyle = pat;
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
}

// ── Preset backdrop images served from R2 (files.sohozkaj.com/remove-bg-presets) ─
// Named backdrop scenes (balcony, cafe, office, …). The id is persisted as a
// `preset:<id>` token, so keep these ids stable.
export const BG_PRESET_IDS = [
    'balcony', 'bookstore', 'cafe', 'city-skyline', 'cozy-home', 'eiffel-night',
    'forest-path', 'green-terrace', 'hallway', 'library', 'living-room',
    'misty-mountains', 'modern-room', 'office', 'snow-lake', 'street-up',
    'tropical-beach', 'wood-wall',
];

export const presetSrc = (id) => `https://files.sohozkaj.com/remove-bg-presets/${id}.webp`;

export const BG_PRESETS = BG_PRESET_IDS.map((id) => ({ id, src: presetSrc(id) }));

// object-fit:cover for an image element within a rect
export function drawImageCover(ctx, img, x, y, w, h) {
    const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    const scale = Math.max(w / iw, h / ih);
    const dw = iw * scale, dh = ih * scale;
    ctx.save();
    ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
    ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
    ctx.restore();
}

// ── Token helpers ─────────────────────────────────────────────────────────────
export function parseBgToken(token) {
    if (!token) return { kind: 'none' };
    if (token === 'image') return { kind: 'image' };
    if (token.startsWith('gradient:')) return { kind: 'gradient', id: token.slice(9) };
    if (token.startsWith('pattern:'))  return { kind: 'pattern',  id: token.slice(8) };
    if (token.startsWith('preset:'))   return { kind: 'preset',   id: token.slice(7) };
    return { kind: 'solid', value: token };
}

export const isSpecialBgToken = (token) => {
    const k = parseBgToken(token).kind;
    return k === 'gradient' || k === 'pattern' || k === 'preset' || k === 'image';
};

// True when an image's stored file is a transparent cut-out (alpha), so the UI must
// paint the `background` token behind it (checkerboard for 'transparent', else the
// colour). Two cases: manual-editor outputs (flagged `manuallyEdited`) and AI results
// (now always generated transparent) — those are uploaded as .webp/.png, so the AI
// image URL extension is the reliable signal (opaque legacy results are .jpg).
export const hasTransparentSubject = (image) => {
    if (!image) return false;
    if (image.manuallyEdited) return true;
    const url = image.aiImage || image.aiImagePath || image.editedImagePath || image.url || '';
    return /\.(png|webp)(\?|#|$)/i.test(url);
};

// CSS style object for a special token (gradient / pattern / preset). Returns null
// for solid/image tokens — callers handle those with their own state (color / src).
export function specialBgCss(token) {
    const d = parseBgToken(token);
    if (d.kind === 'gradient') {
        const g = BG_GRADIENTS.find(x => x.id === d.id);
        return g ? { background: g.css } : null;
    }
    if (d.kind === 'pattern') {
        const p = BG_PATTERNS.find(x => x.id === d.id);
        if (!p) return null;
        const url = buildPatternTile(p).toDataURL();
        return { background: `url(${url})`, backgroundRepeat: 'repeat', backgroundSize: `${p.tile}px ${p.tile}px` };
    }
    if (d.kind === 'preset') {
        return { background: `center/cover no-repeat url(${presetSrc(d.id)})` };
    }
    return null;
}

// Paint a gradient/pattern token onto a canvas rect. Image-based tokens
// (preset/upload) require a loaded element and are handled by the caller.
export function paintSpecialBg(ctx, x, y, w, h, token) {
    const d = parseBgToken(token);
    if (d.kind === 'gradient') {
        const g = BG_GRADIENTS.find(it => it.id === d.id);
        if (g) { paintGradient(ctx, x, y, w, h, g); return true; }
    } else if (d.kind === 'pattern') {
        const p = BG_PATTERNS.find(it => it.id === d.id);
        if (p) { paintPattern(ctx, x, y, w, h, p); return true; }
    }
    return false;
}

// Named bg id → hex (shared across preview modal, /documents, dashboard).
export const BG_HEX = {
    white: '#ffffff', blue: '#2196F3', sky_blue: '#03A9F4', grey: '#9E9E9E',
    dark_blue: '#3068E1', teal: '#00897B', off_white: '#FFF8DC',
};

const _loadImg = (src) => new Promise((res, rej) => {
    const i = new window.Image(); i.crossOrigin = 'anonymous';
    i.onload = () => res(i); i.onerror = rej; i.src = src;
});
// Preset/CDN backgrounds cache without CORS headers; fetch fresh in CORS mode and load
// from a same-origin blob URL so drawing never taints the export canvas.
const _loadCorsImg = async (src) => {
    const resp = await fetch(src, { mode: 'cors', cache: 'reload' });
    if (!resp.ok) throw new Error(`bg fetch failed: ${resp.status}`);
    const objUrl = URL.createObjectURL(await resp.blob());
    try {
        return await new Promise((res, rej) => {
            const i = new window.Image();
            i.onload = () => res(i); i.onerror = () => rej(new Error('bg image decode failed'));
            i.src = objUrl;
        });
    } finally { setTimeout(() => URL.revokeObjectURL(objUrl), 5000); }
};

/**
 * Composite a transparent-subject image's stored `background` token behind it so a
 * download/copy matches what the preview shows. Handles solid/named hex, gradient,
 * pattern and preset-image backgrounds. Kept transparent → returns a PNG (preserves
 * alpha, strips WebP so it opens everywhere). Unknown token → original blob.
 */
export async function compositeBackground(blob, bg) {
    if (!bg || bg === 'transparent') {
        if (blob.type === 'image/png') return blob;
        try {
            const subj = await _loadImg(URL.createObjectURL(blob));
            const c = document.createElement('canvas');
            c.width = subj.naturalWidth; c.height = subj.naturalHeight;
            c.getContext('2d').drawImage(subj, 0, 0);
            return await new Promise((res, rej) => c.toBlob(b => b ? res(b) : rej(new Error('toBlob failed')), 'image/png'));
        } catch { return blob; }
    }
    const subject = await _loadImg(URL.createObjectURL(blob));
    const w = subject.naturalWidth, h = subject.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');

    const d = parseBgToken(bg);
    if (d.kind === 'gradient' || d.kind === 'pattern') {
        paintSpecialBg(ctx, 0, 0, w, h, bg);
    } else if (d.kind === 'preset') {
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h);
        try { drawImageCover(ctx, await _loadCorsImg(presetSrc(d.id)), 0, 0, w, h); } catch { /* keep white */ }
    } else {
        const hex = BG_HEX[bg] || (bg.startsWith('#') || bg.startsWith('rgb') ? bg : null);
        if (!hex) return blob; // unknown token — return as-is
        ctx.fillStyle = hex; ctx.fillRect(0, 0, w, h);
    }
    ctx.drawImage(subject, 0, 0, w, h);
    return new Promise((res, rej) =>
        canvas.toBlob(b => b ? res(b) : rej(new Error('canvas toBlob failed')), 'image/jpeg', 0.97));
}
